import { cn } from '@/lib/utils';
import { veckonummer } from '@/lib/veckor';

export interface Veckopunkt {
  /** Måndag i veckan, YYYY-MM-DD (svensk tid). */
  vecka: string;
  antal: number;
}

/**
 * Tolv veckor som små staplar. Staplar och inte linje: datan är gles (flera
 * nollveckor), och en linje mellan nollor och enstaka toppar ser ut som brus.
 *
 * Bara innevarande vecka har full färg – den enda stapeln som fortfarande
 * växer. Övriga är tonade, så att blicken landar på "nu" och inte på
 * gamla toppar.
 *
 * Vanliga element i flex, inte SVG: en utsträckt viewBox gjorde de rundade
 * hörnen ovala och gav staplarna olika bredd på olika kort.
 */
export function Veckostaplar({
  serie,
  enhet,
  rubrik,
  className,
}: {
  serie: Veckopunkt[];
  /** Plural, i löptext: "utkast", "nya luckor". */
  enhet: string;
  /** Står mellan veckonumren, så att staplarna inte läses som kortets total. */
  rubrik: string;
  className?: string;
}) {
  if (serie.length === 0) return null;

  const max = Math.max(1, ...serie.map((p) => p.antal));
  const sista = serie[serie.length - 1];
  const summa = serie.reduce((s, p) => s + p.antal, 0);

  return (
    <figure className={cn('min-w-0', className)}>
      <div
        role="img"
        aria-label={`${summa} ${enhet} de senaste ${serie.length} veckorna, ${sista.antal} hittills vecka ${veckonummer(sista.vecka)}.`}
        className="h-11 flex items-end gap-[3px] border-b border-ink-200"
      >
        {serie.map((p, i) => {
          const nu = i === serie.length - 1;
          return (
            <div
              key={p.vecka}
              title={`Vecka ${veckonummer(p.vecka)}: ${p.antal} ${enhet}`}
              className="flex-1 h-full flex items-end"
            >
              {p.antal > 0 && (
                <div
                  className={cn('stapel-vaxer w-full rounded-t-[2px] bg-brand-500', !nu && 'opacity-40')}
                  style={{ height: `${Math.max(5, (p.antal / max) * 100)}%`, animationDelay: `${i * 35}ms` }}
                />
              )}
            </div>
          );
        })}
      </div>
      <figcaption className="mt-1 flex items-baseline justify-between gap-2 text-[10.5px] text-ink-500 tabular-nums" aria-hidden>
        <span>v{veckonummer(serie[0].vecka)}</span>
        <span className="truncate">{rubrik}</span>
        <span>v{veckonummer(sista.vecka)}</span>
      </figcaption>
    </figure>
  );
}
